import React, { useState } from "react";
import "./App.css";
import {
  usePingPongEngine,
  GAME_WIDTH,
  GAME_HEIGHT,
  PADDLE_WIDTH,
  Difficulty,
} from "./usePingPongEngine";

const project3D = (x: number, y: number) => {
  const percentY = y / GAME_HEIGHT;
  const topWidth = 160;
  const bottomWidth = 260;
  const topY = 40;
  const bottomY = 260;

  const py = topY + percentY * (bottomY - topY);
  const widthAtY = topWidth + percentY * (bottomWidth - topWidth);
  const px = 150 + ((x - GAME_WIDTH / 2) / (GAME_WIDTH / 2)) * (widthAtY / 2);

  const scale = widthAtY / bottomWidth;
  return { x: px, y: py, scale };
};

export default function App() {
  const [isPlayingMode, setIsPlayingMode] = useState<boolean>(false);
  const [tableColor, setTableColor] = useState<"blue" | "green">("blue");
  const [ballColor, setBallColor] = useState<"white" | "yellow">("white");
  const [targetSets, setTargetSets] = useState<number>(3);
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");

  const {
    gameState,
    handlePointerMove,
    handlePointerDown,
    handlePointerUp,
    resetMatch,
  } = usePingPongEngine(targetSets, difficulty);

  const tableTopColor = tableColor === "blue" ? "#1b447a" : "#1a5c38";
  const tableSideColor = tableColor === "blue" ? "#0f274a" : "#0d361f";
  const ballFillColor = ballColor === "white" ? "#f8f9fa" : "#ffc107";

  const topLeft = project3D(0, 0);
  const topRight = project3D(GAME_WIDTH, 0);
  const bottomLeft = project3D(0, GAME_HEIGHT);
  const bottomRight = project3D(GAME_WIDTH, GAME_HEIGHT);
  const midTop = project3D(GAME_WIDTH / 2, 0);
  const midBottom = project3D(GAME_WIDTH / 2, GAME_HEIGHT);

  const netLeft = project3D(0, GAME_HEIGHT / 2);
  const netRight = project3D(GAME_WIDTH, GAME_HEIGHT / 2);
  const netHeight = 12;

  const ballPos = project3D(gameState.ball.x, gameState.ball.y);
  const playerPos = project3D(gameState.playerX, gameState.playerY);
  const aiPos = project3D(gameState.aiX, gameState.aiY);

  const renderPaddle = (
    pos: { x: number; y: number; scale: number },
    isPlayer: boolean,
  ) => {
    const pWidth = PADDLE_WIDTH * pos.scale;
    const pHeight = 28 * pos.scale;
    const hWidth = 5 * pos.scale;
    const hHeight = 14 * pos.scale;
    const rubber = isPlayer ? "#d32f2f" : "#212121";

    const velX = isPlayer ? gameState.playerVelX : gameState.aiVelX;
    const velY = isPlayer ? gameState.playerVelY : 0;

    const rotateZ = velX * 4.0;
    const skewX = velX * 1.8;
    const tiltY = isPlayer ? Math.max(-25, Math.min(8, velY * 5.0)) : 0;

    return (
      <g
        transform={`translate(${pos.x}, ${pos.y - pHeight / 2}) rotate(${rotateZ}) skewX(${skewX})`}
        style={{ transition: "transform 0.04s ease-out" }}
      >
        <ellipse
          cx={0}
          cy={pHeight / 2 + (isPlayer ? tiltY * 0.2 : 0)}
          rx={pWidth / 1.5}
          ry={3 * pos.scale}
          fill="rgba(0,0,0,0.25)"
          filter="url(#blur)"
        />
        <rect
          x={-hWidth / 2}
          y={pos.y > 150 ? 0 : -hHeight}
          width={hWidth}
          height={hHeight + (isPlayer && velY < -0.2 ? 2 : 0)}
          fill="#c78c52"
          rx={1}
        />
        <ellipse
          cx={0}
          cy={pos.y > 150 ? -pHeight / 4 + tiltY * 0.3 : pHeight / 4}
          rx={pWidth / 2}
          ry={Math.max(
            2 * pos.scale,
            (pHeight / 2) * (1 - Math.abs(tiltY) * 0.02),
          )}
          fill={rubber}
          stroke="#fff"
          strokeWidth={0.5 * pos.scale}
        />
      </g>
    );
  };

  if (!isPlayingMode) {
    return (
      <div className="menu-container">
        <h1 className="menu-title">SOFTY PING PONG</h1>
        <div className="setup-card">
          <h2 className="setup-title">CẤU HÌNH TRẬN ĐẤU</h2>

          <div className="setup-group">
            <label>Màu bàn bóng bàn</label>
            <select
              value={tableColor}
              onChange={(e) => setTableColor(e.target.value as any)}
            >
              <option value="blue">Bàn DHS - Xanh Dương</option>
              <option value="green">Bàn Double Fish - Xanh Lá</option>
            </select>
          </div>

          <div className="setup-group">
            <label>Màu quả bóng</label>
            <select
              value={ballColor}
              onChange={(e) => setBallColor(e.target.value as any)}
            >
              <option value="white">Bóng Nittaku - Trắng</option>
              <option value="yellow">Bóng Nittaku - Vàng</option>
            </select>
          </div>

          <div className="setup-group">
            <label>Trình độ Máy đấu (AI Level)</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as Difficulty)}
            >
              <option value="easy">Tay mơ</option>
              <option value="medium">Cao thủ</option>
              <option value="hard">Kiện tướng</option>
            </select>
          </div>

          <div className="setup-group">
            <label>Thể thức trận đấu (Số Sets thắng)</label>
            <select
              value={targetSets}
              onChange={(e) => setTargetSets(Number(e.target.value))}
            >
              <option value={1}>Chạm 1 (Đấu đơn nhanh)</option>
              <option value={3}>Chạm 3 (Best of 5)</option>
              <option value={4}>Chạm 4 (Best of 7 - Olympic)</option>
            </select>
          </div>

          <button
            className="start-game-btn"
            onClick={() => setIsPlayingMode(true)}
          >
            BẮT ĐẦU CHƠI
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <div className="score-board">
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: "0.75rem", color: "#666" }}>MÁY (AI)</div>
          <span>{String(gameState.score.ai).padStart(2, "0")}</span>
          <span
            style={{ fontSize: "1rem", color: "#ffa500", marginLeft: "10px" }}
          >
            [S: {gameState.sets.ai}/{targetSets}]
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", color: "#444" }}>
          VS
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "0.75rem", color: "#666" }}>BẠN</div>
          <span
            style={{ fontSize: "1rem", color: "#ffa500", marginRight: "10px" }}
          >
            [S: {gameState.sets.player}/{targetSets}]
          </span>
          <span>{String(gameState.score.player).padStart(2, "0")}</span>
        </div>
      </div>

      <svg
        className="game-board"
        viewBox="0 0 300 300"
        preserveAspectRatio="xMidYMid meet"
        onPointerMove={handlePointerMove}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{ overflow: "visible" }}
      >
        <defs>
          <filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" />
          </filter>
          <pattern
            id="netMesh"
            width="2"
            height="2"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 2 0 L 0 0 0 2"
              fill="none"
              stroke="#fff"
              strokeWidth="0.2"
              opacity="0.7"
            />
          </pattern>
        </defs>

        <polygon
          points={`${topLeft.x - 5},${topLeft.y + 5} ${topRight.x + 5},${topRight.y + 5} ${bottomRight.x + 15},${bottomRight.y + 10} ${bottomLeft.x - 15},${bottomLeft.y + 10}`}
          fill="rgba(0,0,0,0.5)"
          filter="url(#blur)"
        />
        <polygon
          points={`${bottomLeft.x},${bottomLeft.y} ${topLeft.x},${topLeft.y} ${topLeft.x},${topLeft.y + 5} ${bottomLeft.x},${bottomLeft.y + 10}`}
          fill={tableSideColor}
        />
        <polygon
          points={`${bottomRight.x},${bottomRight.y} ${topRight.x},${topRight.y} ${topRight.x},${topRight.y + 5} ${bottomRight.x},${bottomRight.y + 10}`}
          fill={tableSideColor}
        />
        <polygon
          points={`${bottomLeft.x},${bottomLeft.y} ${bottomRight.x},${bottomRight.y} ${bottomRight.x},${bottomRight.y + 10} ${bottomLeft.x},${bottomLeft.y + 10}`}
          fill={tableSideColor}
        />
        <polygon
          points={`${topLeft.x},${topLeft.y} ${topRight.x},${topRight.y} ${bottomRight.x},${bottomRight.y} ${bottomLeft.x},${bottomLeft.y}`}
          fill={tableTopColor}
        />
        <polygon
          points={`${topLeft.x},${topLeft.y} ${topRight.x},${topRight.y} ${bottomRight.x},${bottomRight.y} ${bottomLeft.x},${bottomLeft.y}`}
          fill="none"
          stroke="#fff"
          strokeWidth="1.5"
        />
        <line
          x1={midTop.x}
          y1={midTop.y}
          x2={midBottom.x}
          y2={midBottom.y}
          stroke="#fff"
          strokeWidth="1"
          opacity="0.8"
        />

        <g>
          <polygon
            points={`${netLeft.x},${netLeft.y} ${netRight.x},${netRight.y} ${netRight.x},${netRight.y - netHeight} ${netLeft.x},${netLeft.y - netHeight}`}
            fill="url(#netMesh)"
            stroke="#fff"
            strokeWidth="0.5"
          />
          <rect
            x={netLeft.x - 2}
            y={netLeft.y - netHeight}
            width={2}
            height={netHeight + 2}
            fill="#222"
          />
          <rect
            x={netRight.x}
            y={netRight.y - netHeight}
            width={2}
            height={netHeight + 2}
            fill="#222"
          />
        </g>

        {renderPaddle(aiPos, false)}

        {!gameState.isPlaying &&
          gameState.server === "player" &&
          gameState.tossPhase === "idle" &&
          !gameState.matchWinner && (
            <text
              x="150"
              y="160"
              fill="#fff"
              fontSize="9"
              fontFamily="Arial"
              textAnchor="middle"
              opacity="0.8"
            >
              CHẠM VÀO BÀN ĐỂ TUNG BÓNG
            </text>
          )}

        <g transform={`translate(${ballPos.x}, ${ballPos.y})`}>
          <ellipse
            cx={0}
            cy={3}
            rx={4 * ballPos.scale}
            ry={2 * ballPos.scale}
            fill="rgba(0,0,0,0.4)"
            filter="url(#blur)"
            opacity={Math.max(0.1, 1 - gameState.ball.z / 25)}
          />
          <g
            transform={`translate(0, ${-gameState.ball.z * 2 * ballPos.scale})`}
          >
            <circle
              cx={0}
              cy={-2}
              r={3.5 * ballPos.scale}
              fill={ballFillColor}
            />
            <circle
              cx={-1 * ballPos.scale}
              cy={-3 * ballPos.scale}
              r={1 * ballPos.scale}
              fill="#fff"
              opacity="0.6"
            />
          </g>
        </g>

        {renderPaddle(playerPos, true)}

        {gameState.matchWinner && (
          <g style={{ cursor: "default" }}>
            <rect width="300" height="300" fill="#000" opacity="0.9" />
            <text
              x="150"
              y="100"
              fill="#ffa500"
              fontSize="14"
              fontFamily="Arial"
              textAnchor="middle"
              fontWeight="bold"
            >
              TRẬN ĐẤU KẾT THÚC
            </text>
            <text
              x="150"
              y="125"
              fill="#fff"
              fontSize="12"
              fontFamily="Arial"
              textAnchor="middle"
              fontWeight="bold"
            >
              {gameState.matchWinner === "player"
                ? "BẠN ĐÃ THẮNG CUỘC!"
                : "MÁY (AI) THẮNG CUỘC!"}
            </text>
            <text
              x="150"
              y="145"
              fill="#aaa"
              fontSize="10"
              fontFamily="Arial"
              textAnchor="middle"
            >
              Tỉ số hiệp: {gameState.sets.player} - {gameState.sets.ai}
            </text>

            <g
              onPointerDown={(e) => {
                e.stopPropagation();
                resetMatch();
              }}
              style={{ cursor: "pointer" }}
            >
              <rect
                x="95"
                y="175"
                width="110"
                height="24"
                fill="#d32f2f"
                rx="4"
              />
              <text
                x="150"
                y="191"
                fill="#fff"
                fontSize="9"
                fontFamily="Arial"
                textAnchor="middle"
                fontWeight="bold"
              >
                ĐẤU LẠI LƯỢT NÀY
              </text>
            </g>

            <g
              onPointerDown={(e) => {
                e.stopPropagation();
                resetMatch();
                setIsPlayingMode(false);
              }}
              style={{ cursor: "pointer" }}
            >
              <rect
                x="95"
                y="210"
                width="110"
                height="24"
                fill="#444"
                rx="4"
                stroke="#666"
                strokeWidth="0.5"
              />
              <text
                x="150"
                y="226"
                fill="#fff"
                fontSize="9"
                fontFamily="Arial"
                textAnchor="middle"
                fontWeight="bold"
              >
                THAY ĐỔI CÀI ĐẶT
              </text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
